<template>
  <div class="faqsets-list">
    <HeadSection
      title="FAQsets"
      :permissionOfSection="permissionOfSection"
    >
      <template #actions>
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission"
            @click="createFaqset"
          >
            <a-icon type="plus" /> Add
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="filters">
        <UniversalSelect
          v-model="filters.role"
          style="width: 180px;"
          :options="rolesOptions"
          placeholder="Selected role"
          title="Role"
          :disabled="loadingFaqsets || loadingRoles || !hasListData"
          @change="changeRoleFilter"
        />
        <div class="d-flex align-items-end" style="height: 62px;">
          <SearchField
            :value="filters.name"
            :loading="loadingFaqsets"
            :disabled="loadingFaqsets || !hasListData"
            @search="changeSearchValue"
          />
        </div>
        <a-button
          v-if="isFiltered"
          size="large"
          :disabled="loadingFaqsets"
          @click="() => handleReset()"
        >
          Reset filters
        </a-button>
      </div>
      <a-table
        class="faqsets-table"
        :dataSource="faqsetsListData"
        :pagination="faqsetsListMeta"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :columns="faqsetsColumns"
        :loading="loadingFaqsets"
        @change="changePagination"
      >
        <template slot="name" slot-scope="name, record">
          <div class="faqsets-link">
            <router-link class="text-primary" :to="`/referral/faqsets/${record.id}/view`">{{ name }}</router-link>
          </div>
        </template>
        <template slot="role" slot-scope="role">
          <a-badge
            v-if="role && role?.name"
            :count="role.name"
            style="cursor: default; transform: scale(1.2);"
            :number-style="{
              backgroundColor: role.name === 'Referral' ? '#52c41a' : '#108ee9',
              boxShadow: '0 4px 8px 0 #d9d9d9',
            }"
          />
          <span v-else>—</span>
        </template>
      </a-table>
      <TotalCount :count="faqsetsListMeta.total" />
    </MainContainer>
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import SearchField from '@/components/custom/form/SearchField.vue'
import TotalCount from '@/views/apps/referral-program/components/TotalCount.vue'
import { getReferralRoles } from '@/views/apps/referral-program/composables/useGetReferralRoles.js'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    UniversalSelect,
    SearchField,
    TotalCount,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    rolesOptions: [],
    filters: {
      role: undefined,
      page: undefined,
      per_page: undefined,
      name: undefined,
    },
    faqsetsListData: [],
    faqsetsListMeta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    loadingFaqsets: false,
    loadingRoles: false,
    faqsetsColumns: [
      {
        title: 'Name',
        dataIndex: 'name',
        width: '20%',
        ellipsis: true,
        scopedSlots: { customRender: 'name' },
      },
      {
        title: 'Role',
        dataIndex: 'role',
        width: '30%',
        scopedSlots: { customRender: 'role' },
      },
      {
        title: 'Items',
        dataIndex: 'items',
        width: '15%',
        // sorter: true, // TODO: пока нету на бэке, потом добавить
      },
    ],
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isHasListItems() {
      return !!this.faqsetsListData?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
  },
  methods: {
    customRow(referral) {
      return {
        on: {
          click: () => {
            this.$router.push(`/referral/faqsets/${referral.id}/view`)
          },
        },
      }
    },
    createFaqset() {
      this.$router.push('/referral/faqsets/create')
    },
    changeRoleFilter(roleValue) {
      this.filters.role = roleValue
      this.getFaqsets()
    },
    changeSearchValue(value) {
      this.filters.name = value
      this.getFaqsets()
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize
      this.getFaqsets()
    },
    getFilters() {
      setQueryParams(this.filters, this)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters })
    },
    async getFaqsets() {
      try {
        this.loadingFaqsets = true

        const filters = this.getFilters()

        const url = `${referralApiUrl}/faq-sets`
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.faqsetsListData = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.faqsetsListMeta, newMeta)
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loadingFaqsets = false
      }
    },
    async getRoles() {
      try {
        this.loadingRoles = true
        const roles = await getReferralRoles()
        if (roles) {
          this.rolesOptions = roles.map(role => {
            return {
              name: role.name,
              value: role.id,
            }
          })
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loadingRoles = false
      }
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.getFaqsets()
    },
  },
  async created() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this)
    }
    this.getFaqsets()
    this.getRoles()
  },
}
</script>

<style lang="scss">
.faqsets-list {
  .filters {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-bottom: 10px;
  }
}
.faqsets-table {
  .faqsets-link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .ant-table-row {
    cursor: pointer;
  }
  &__edit-mode {
    .ant-table-row {
      cursor: default;
    }
  }
}
[data-kit-theme="default"] .ant-table-empty .ant-table-body {
  overflow: visible !important;
}
.ant-table-column-has-actions {
  overflow: visible !important;
}
</style>
